import styled, { keyframes } from "styled-components";
import { color } from "../../../shared/styles";

const modalFadeIn = keyframes`
    from {
        opacity: 0;
        transform: translate(-50%, -54%);
    }
    to {
        opacity: 1;
        transform: translate(-50%, -50%);
    }
`;

const modalFadeOut = keyframes`
    from {
        opacity: 1;
        transform: translate(-50%, -50%);
    }
    to {
        opacity: 0;
        transform: translate(-50%, -54%);
    }
`;

export const OutsourceStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

export const OutsourceWorkflowShell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const OutsourceWorkflowHeader = styled.div`
    padding: 14px 16px;
    border-radius: 8px;
    border: 1px solid ${color.secondary.BORDER};
    background:
        linear-gradient(135deg, rgba(0, 88, 160, 0.08), rgba(255, 255, 255, 0) 52%),
        ${color.emphasis.NEUTRAL_N0};
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    align-items: start;
`;

export const WorkflowTitleBlock = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

export const WorkflowTitle = styled.div`
    color: ${color.secondary.CHARCOAL};
    font-size: 18px;
    font-weight: 800;
    line-height: 24px;
`;

export const WorkflowSubtitle = styled.div`
    max-width: 760px;
    color: ${color.emphasis.NEUTRAL_N70};
    font-size: 13px;
    font-weight: 600;
    line-height: 19px;
`;

export const WorkflowRail = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

export const WorkflowStep = styled.div<{ $active?: boolean }>`
    min-width: 0;
    padding: 8px 10px;
    border-radius: 7px;
    border: 1px solid ${({ $active }) => ($active ? color.primary.BLUE_B100 : color.secondary.BORDER)};
    background-color: ${({ $active }) => ($active ? color.status.INFO_LIGHT : color.emphasis.NEUTRAL_N0)};
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const WorkflowStepNumber = styled.div<{ $active?: boolean }>`
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background-color: ${({ $active }) => ($active ? color.primary.BLUE_B100 : color.secondary.LIGHT_GREY)};
    color: ${({ $active }) => ($active ? color.emphasis.NEUTRAL_N0 : color.secondary.CHARCOAL)};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 800;
    flex: 0 0 auto;
`;

export const WorkflowStepText = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;

    strong {
        color: ${color.secondary.CHARCOAL};
        font-size: 13px;
        font-weight: 800;
        line-height: 17px;
    }

    span {
        color: ${color.emphasis.NEUTRAL_N70};
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
    }
`;

export const WorkflowGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 2.1fr) minmax(320px, 0.7fr);
    gap: 18px;
    align-items: start;

    @media (max-width: 1100px) {
        grid-template-columns: 1fr;
    }
`;

export const WorkflowPrimaryColumn = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

export const WorkflowStageSplit = styled.div`
    min-width: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 18px;
    align-items: start;

    @media (max-width: 980px) {
        grid-template-columns: 1fr;
    }
`;

export const WorkflowSideColumn = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

export const WorkflowSummaryBanner = styled.div`
    padding: 10px 12px;
    border-radius: 7px;
    border: 1px solid ${color.secondary.BORDER};
    background-color: ${color.emphasis.NEUTRAL_N5};
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

export const WorkflowMetric = styled.div`
    min-width: 0;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
`;

export const WorkflowMetricLabel = styled.div`
    color: ${color.emphasis.NEUTRAL_N70};
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
`;

export const WorkflowMetricValue = styled.div`
    color: ${color.secondary.CHARCOAL};
    font-size: 16px;
    font-weight: 800;
    line-height: 22px;
`;

export const WorkflowTabList = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    align-items: stretch;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

export const WorkflowTabButton = styled.button<{ $active?: boolean }>`
    min-width: 0;
    min-height: 58px;
    padding: 10px 12px;
    border-radius: 7px;
    border: 1px solid ${({ $active }) => ($active ? color.primary.BLUE_B100 : color.secondary.BORDER)};
    background-color: ${({ $active }) => ($active ? color.status.INFO_LIGHT : color.emphasis.NEUTRAL_N0)};
    color: ${color.secondary.CHARCOAL};
    cursor: pointer;
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    column-gap: 10px;
    row-gap: 2px;
    align-items: center;
    text-align: left;
    transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        border-color: ${color.primary.BLUE_B100};
        box-shadow: 0 2px 8px rgba(16, 47, 67, 0.1);
    }

    &:focus-visible {
        outline: 2px solid rgba(0, 88, 160, 0.28);
        outline-offset: 2px;
    }
`;

export const WorkflowTabNumber = styled.span`
    grid-row: 1 / span 2;
    width: 30px;
    height: 30px;
    border-radius: 999px;
    background-color: ${color.secondary.LIGHT_GREY};
    color: ${color.secondary.CHARCOAL};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 800;
`;

export const WorkflowTabText = styled.span`
    min-width: 0;
    color: ${color.secondary.CHARCOAL};
    font-size: 14px;
    font-weight: 800;
    line-height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const WorkflowTabMeta = styled.span`
    min-width: 0;
    color: ${color.emphasis.NEUTRAL_N70};
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ActiveWorkflowStage = styled.div`
    min-width: 0;
    width: 100%;
`;

export const OutsourcePanel = styled.div`
    background-color: ${color.emphasis.NEUTRAL_N0};
    border-radius: 6px;
    min-height: fit-content;
    border: 1px solid ${color.secondary.BORDER};
    box-shadow: none;
    height: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;

    .text {
        font-family: Noto Sans;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
    }
`;

export const OutsourcePanelTitle = styled.div`
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    color: ${color.secondary.CHARCOAL};
`;

export const WorkflowSupportBand = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(220px, 1fr));
    gap: 12px;
    align-items: start;

    & > div {
        min-width: 0;
        padding: 16px;
        gap: 14px;
    }

    & > div > div:first-child {
        font-size: 16px;
        font-weight: 700;
        line-height: 22px;
    }

    textarea {
        min-height: 84px;
        font-size: 14px;
        line-height: 20px;
    }

    @media (max-width: 1120px) {
        grid-template-columns: repeat(2, minmax(220px, 1fr));
    }

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

export const LedgerSection = styled.div`
    min-width: 0;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 8px;
    background:
        linear-gradient(180deg, rgba(0, 88, 160, 0.035), rgba(255, 255, 255, 0) 90px),
        ${color.emphasis.NEUTRAL_N0};
    border: 1px solid ${color.secondary.BORDER};
    box-shadow: none;
    min-height: 180px;
`;

export const TruncatedCellText = styled.div`
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 28px;
`;

export const NumericCellText = styled.div`
    min-width: max-content;
    white-space: nowrap;
    font-weight: 600;
    line-height: 28px;
`;

export const SummaryRows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const SummaryLine = styled.div<{ $strong?: boolean }>`
    min-width: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    gap: 16px;
    align-items: center;
    color: ${color.secondary.CHARCOAL};
    font-size: ${({ $strong }) => ($strong ? "15px" : "14px")};
    font-weight: ${({ $strong }) => ($strong ? 800 : 600)};
    line-height: 20px;
`;

export const SummaryLineLabel = styled.div`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const SummaryLineValue = styled.div`
    white-space: nowrap;
    text-align: right;
    font-variant-numeric: tabular-nums;
`;

export const LedgerSectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
`;

export const LedgerTitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

export const StageLabel = styled.div`
    width: fit-content;
    padding: 3px 8px;
    border-radius: 999px;
    background-color: ${color.secondary.LIGHT_GREY};
    border: 1px solid ${color.secondary.BORDER};
    color: ${color.emphasis.NEUTRAL_N70};
    font-size: 11px;
    font-weight: 800;
    line-height: 15px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

export const LedgerTitle = styled.div`
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    color: ${color.secondary.CHARCOAL};
`;

export const LedgerSubtitle = styled.div`
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    color: ${color.emphasis.NEUTRAL_N70};
`;

export const CountBadge = styled.div`
    min-width: 44px;
    padding: 4px 10px;
    border-radius: 999px;
    background-color: ${color.secondary.LIGHT_GREY};
    color: ${color.secondary.CHARCOAL};
    border: 1px solid ${color.secondary.BORDER};
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
`;

export const EmptyState = styled.div`
    min-height: 76px;
    padding: 16px;
    border: 1px dashed ${color.secondary.GREY_4};
    border-radius: 6px;
    background-color: ${color.emphasis.NEUTRAL_N5};
    display: flex;
    flex-direction: column;
    gap: 4px;
    justify-content: center;
    color: ${color.secondary.CHARCOAL};

    .title {
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
    }

    .body {
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
        color: ${color.emphasis.NEUTRAL_N70};
    }
`;

export const OperationModalFrame = styled.div<{ $isOpen: boolean }>`
    width: min(920px, 92vw);
    max-height: 88vh;
    padding: 24px;
    border-radius: 8px;
    background-color: ${color.emphasis.NEUTRAL_N0};
    border: 1px solid ${color.secondary.BORDER};
    box-sizing: border-box;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    gap: 18px;
    font-family: Noto Sans;
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    animation: ${({ $isOpen }) => ($isOpen ? modalFadeIn : modalFadeOut)} 0.22s ease-out forwards;

    @media (max-width: 720px) {
        width: 94vw;
        min-width: 0;
        padding: 18px;
    }
`;

export const OperationModalBody = styled.div`
    min-height: 0;
    max-height: calc(88vh - 150px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const OperationModalHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
`;

export const OperationTitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const OperationTitle = styled.div`
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
    color: ${color.secondary.CHARCOAL};
`;

export const OperationSubtitle = styled.div`
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    color: ${color.emphasis.NEUTRAL_N70};
`;

export const OperationSummaryStrip = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;

    @media (max-width: 760px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

export const OperationImpactGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(260px, 1.1fr) minmax(0, 1.4fr);
    gap: 14px;
    align-items: stretch;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

export const OperationPrimaryPanel = styled.div`
    min-width: 0;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid ${color.primary.BLUE_B100};
    background-color: ${color.status.INFO_LIGHT};
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const OperationPrimaryLabel = styled.div`
    color: ${color.primary.BLUE_B100};
    font-size: 13px;
    font-weight: 800;
    line-height: 18px;
`;

export const OperationPrimaryValue = styled.div`
    color: ${color.secondary.CHARCOAL};
    font-size: 26px;
    font-weight: 800;
    line-height: 32px;
    overflow-wrap: anywhere;
`;

export const OperationWarning = styled.div`
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid ${color.status.WARNING};
    background-color: ${color.status.WARNING_LIGHT};
    color: ${color.secondary.CHARCOAL};
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
`;

export const OperationMetric = styled.div`
    min-width: 0;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid ${color.secondary.BORDER};
    background-color: ${color.emphasis.NEUTRAL_N5};
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const OperationMetricLabel = styled.div`
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    color: ${color.emphasis.NEUTRAL_N70};
`;

export const OperationMetricValue = styled.div`
    font-size: 16px;
    font-weight: 800;
    line-height: 22px;
    color: ${color.secondary.CHARCOAL};
    overflow-wrap: anywhere;
`;

export const OperationPanel = styled.div`
    padding: 14px;
    border-radius: 6px;
    border: 1px solid ${color.secondary.BORDER};
    background-color: ${color.emphasis.NEUTRAL_N0};
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const OperationPanelTitle = styled.div`
    font-size: 15px;
    font-weight: 800;
    line-height: 20px;
    color: ${color.secondary.CHARCOAL};
`;

export const OperationFieldGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(240px, 1.2fr) minmax(160px, 0.8fr);
    gap: 12px;
    align-items: end;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

export const OperationField = styled.label`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    color: ${color.secondary.CHARCOAL};
`;

export const OperationFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 4px;
    margin-top: auto;

    @media (max-width: 600px) {
        flex-direction: column-reverse;
    }
`;
